import React, { useState, useEffect } from "react";
import { getUserExamResponses } from "../../../services/examResponseService";
import { TimeUtils } from "../../../utils/TimeUtils";
// Assuming your service function is exported from an API folder
// import { fetchExamAttempts } from '../services/api';

export default function ExamAttemptDialog({
  userId,
  syllabusId,
  sessionId,
  isOpen,
  onClose,
}) {
  const [attempts, setAttempts] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Trigger fetch when dialog opens, but only if it hasn't fetched before
  useEffect(() => {
    if (isOpen && !hasFetched && userId) {
      loadAttemptData();
    }
  }, [isOpen, hasFetched, userId]);

  const loadAttemptData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Replace this with your actual service function call
      // const response = await fetchExamAttempts(userId);

      // Simulated delay & Mocking the provided array structure
      const response = await getUserExamResponses(userId);

      // filetr based on syllabusId and sessionId

      const attempts = response.data?.filter(
        (it) =>
          it.metadata.syllabusId == syllabusId &&
          it.metadata.sessionId == sessionId,
      );

      setAttempts(attempts || []);
      setHasFetched(true);
      if (response.length > 0) {
        setSelectedAttempt(response[0]); // Default to first item
      }
    } catch (err) {
      setError("Failed to fetch exam attempts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-6xl h-[95vh] sm:h-[85vh] flex flex-col overflow-hidden border border-slate-100 dark:border-slate-800">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-white">
              Exam Performance Analytics
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">User ID: {userId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            ✕
          </button>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {loading ? (
            <div className="flex-1 flex flex-col items-center justify-center space-y-3">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-slate-500 text-sm font-medium">
                Analyzing test data...
              </p>
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <p className="text-red-500 font-semibold mb-2">{error}</p>
              <button
                onClick={loadAttemptData}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition"
              >
                Retry Connection
              </button>
            </div>
          ) : (
            <>
              {/* Sidebar: Attempts History List */}
              <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-slate-100 dark:border-slate-800 overflow-y-auto p-4 space-y-2 bg-slate-50/50 dark:bg-slate-900 max-h-[25vh] md:max-h-none flex-shrink-0">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block px-2 mb-2 md:mb-3">
                  Attempts History
                </span>
                {attempts
                  .toSorted((a, b) => Number(b.createdAt) - Number(a.createdAt))
                  .map((attempt) => (
                    <button
                      key={attempt.id}
                      onClick={() => setSelectedAttempt(attempt)}
                      className={`w-full text-left p-3.5 rounded-xl transition border text-sm flex flex-col gap-1.5 ${
                        selectedAttempt?.id === attempt.id
                          ? "bg-indigo-50 border-indigo-200 dark:bg-indigo-950/40 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200 shadow-sm"
                          : "bg-white border-slate-100 hover:border-slate-300 dark:bg-slate-800 dark:border-slate-700 text-slate-700 dark:text-slate-300"
                      }`}
                    >
                      <div className="flex justify-between items-start w-full gap-2">
                        <span className="font-semibold text-sm sm:text-base truncate max-w-[70%]">
                          {attempt.exam?.name || attempt.metadata?.chapter}
                        </span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-200/60 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex-shrink-0">
                          {attempt.report?.percentage}%
                        </span>
                      </div>
                      <div className="flex justify-between w-full text-xs text-slate-400 gap-2">
                        <span className="truncate">
                          Subject: {attempt.metadata?.subject}
                        </span>
                        <span className="flex-shrink-0">
                          {TimeUtils.getDateAndTimeFromTimestamp(
                            attempt.createdAt,
                          )}
                        </span>
                      </div>
                    </button>
                  ))}
              </div>

              {/* Main Workspace: Active Analytics View */}
              {selectedAttempt ? (
                <div className="w-full md:w-2/3 overflow-y-auto p-4 sm:p-6 space-y-6 bg-white dark:bg-slate-900">
                  {/* Top Exam Meta Info */}
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
                    <div>
                      <span className="text-xs font-bold px-2.5 py-1 rounded bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">
                        {selectedAttempt.metadata?.subject}
                      </span>
                      <h3 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white mt-2">
                        {selectedAttempt.exam?.name}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Student:{" "}
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {selectedAttempt.user?.name}
                        </span>
                      </p>
                    </div>
                    <div className="text-left sm:text-right text-xs text-slate-400 space-y-1 sm:self-start">
                      <div>
                        Platform:{" "}
                        <span className="capitalize text-slate-600 dark:text-slate-300">
                          {selectedAttempt.metadata?.source}
                        </span>
                      </div>
                      <div>
                        Mode:{" "}
                        <span className="capitalize text-slate-600 dark:text-slate-300">
                          {selectedAttempt.metadata?.attemptMode?.replace(
                            "_",
                            " ",
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Primary Scoring Cards */}
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                      Score & Accuracy
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 p-3 sm:p-4 rounded-xl text-center">
                        <div className="text-xl sm:text-2xl font-black text-emerald-600">
                          {selectedAttempt.report?.correctAnswer}
                        </div>
                        <div className="text-[11px] sm:text-xs font-medium text-emerald-700 dark:text-emerald-400 mt-1">
                          Correct Answers
                        </div>
                      </div>
                      <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/30 p-3 sm:p-4 rounded-xl text-center">
                        <div className="text-xl sm:text-2xl font-black text-rose-600">
                          {selectedAttempt.report?.wrongAnswer}
                        </div>
                        <div className="text-[11px] sm:text-xs font-medium text-rose-700 dark:text-rose-400 mt-1">
                          Wrong Answers
                        </div>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 p-3 sm:p-4 rounded-xl text-center">
                        <div className="text-xl sm:text-2xl font-black text-amber-600">
                          {selectedAttempt.report?.marksObtained}
                        </div>
                        <div className="text-[11px] sm:text-xs font-medium text-amber-700 dark:text-amber-400 mt-1">
                          Marks Obtained
                        </div>
                      </div>
                      <div className="bg-sky-50 dark:bg-sky-950/20 border border-sky-100 dark:border-sky-900/30 p-3 sm:p-4 rounded-xl text-center">
                        <div className="text-xl sm:text-2xl font-black text-sky-600">
                          {selectedAttempt.report?.accuracy}%
                        </div>
                        <div className="text-[11px] sm:text-xs font-medium text-sky-700 dark:text-sky-400 mt-1">
                          Accuracy
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Behavioral Observations & AI Insights */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="border border-slate-100 dark:border-slate-800 p-4 rounded-xl bg-slate-50/50 dark:bg-slate-800/30">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                        Behavioral Profile
                      </h4>
                      <table className="w-full text-sm space-y-2">
                        <tbody>
                          <tr className="border-b border-slate-100 dark:border-slate-800/50">
                            <td className="py-2 text-slate-500">
                              Total System Time:
                            </td>
                            <td className="py-2 font-semibold text-right">
                              {
                                selectedAttempt.behavioralObservations
                                  ?.totalTime
                              }
                              s
                            </td>
                          </tr>
                          <tr className="border-b border-slate-100 dark:border-slate-800/50">
                            <td className="py-2 text-slate-500">
                              Avg Time/Question:
                            </td>
                            <td className="py-2 font-semibold text-right">
                              {
                                selectedAttempt.behavioralObservations
                                  ?.avgTimePerQuestion
                              }
                              s
                            </td>
                          </tr>
                          <tr className="border-b border-slate-100 dark:border-slate-800/50">
                            <td className="py-2 text-slate-500">
                              Rapid Guessing Flags:
                            </td>
                            <td className="py-2 font-semibold text-right text-amber-600">
                              {
                                selectedAttempt.behavioralObservations
                                  ?.rapidGuessingCount
                              }{" "}
                              items
                            </td>
                          </tr>
                          <tr>
                            <td className="py-2 text-slate-500">
                              Focus Score:
                            </td>
                            <td className="py-2 font-semibold text-right text-indigo-600">
                              {
                                selectedAttempt.behavioralObservations
                                  ?.focusScore
                              }
                              %
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="border border-slate-100 dark:border-slate-800 p-4 rounded-xl bg-slate-50/50 dark:bg-slate-800/30 flex flex-col justify-between gap-4">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                          Attempt Insights
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500">
                              Confidence Level:
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-rose-100 text-rose-700">
                              {selectedAttempt.attemptInsights?.confidenceLevel}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500">
                              Risk Profile:
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-red-100 text-red-700">
                              {selectedAttempt.attemptInsights?.riskLevel}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500">
                              Pacing Speed:
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-amber-100 text-amber-700">
                              {selectedAttempt.attemptInsights?.speedPattern?.replace(
                                "_",
                                " ",
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      {selectedAttempt.behavioralObservations
                        ?.fatigueDetected && (
                        <div className="p-2 bg-red-50 text-red-700 rounded text-xs font-medium text-center border border-red-100">
                          ⚠️ Focus Fatigue detected towards tail end of session.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Question Analytics Timelines */}
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                      Item Breakdown Lineup
                    </h4>
                    <div className="space-y-2">
                      {selectedAttempt.questionAnalytics?.map((q, index) => (
                        <div
                          key={q.questionId}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border border-slate-100 dark:border-slate-800 rounded-xl text-sm gap-2"
                        >
                          <div className="flex items-center space-x-3">
                            <span
                              className={`w-6 h-6 flex items-center justify-center font-bold text-xs rounded-full flex-shrink-0 ${
                                q.isCorrect
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-rose-100 text-rose-800"
                              }`}
                            >
                              {index + 1}
                            </span>
                            <div>
                              <span className="font-semibold text-slate-700 dark:text-slate-300 capitalize">
                                {q.difficulty}
                              </span>
                              <span className="mx-2 text-slate-300">|</span>
                              <span className="text-xs text-slate-500 capitalize">
                                {q.questionType}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between sm:justify-end space-x-4 sm:space-x-6 text-xs mt-1 sm:mt-0 pl-9 sm:pl-0">
                            {q.markedForReview && (
                              <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium scale-90">
                                Review
                              </span>
                            )}
                            <div>
                              <span className="text-slate-400 mr-1">
                                Choice:
                              </span>
                              <span className="font-mono font-bold uppercase text-slate-700 dark:text-slate-300">
                                {q.userAnswer || "-"}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400 mr-1">
                                Correct:
                              </span>
                              <span className="font-mono font-bold uppercase text-emerald-600">
                                {q.correctAnswer}
                              </span>
                            </div>
                            <div className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">
                              ⏱️ {q.timeTaken}s
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full md:w-2/3 flex items-center justify-center p-6 text-center text-slate-400">
                  Select an attempt session to display performance metrics.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
